import { Modal, Select } from "antd";
import { Card } from "antd";
import { ContainerModal, ItemTextModal, ItemWrapper, Title } from "../styles";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  EditOutlined,
  SmileOutlined,
  FrownOutlined,
  SearchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useEffect, useState, type ReactNode } from "react";
import { useAtualizarCompatibilidade } from "../hooks/useAtualizarCompatibilidade";
import { useMessage } from "../../../uteis/mensagens/useMessage";
import { IndicacaoParceiro } from "../../../interfaces/IndicacaoParceiro";

const statusOptions = [
  {
    label: (
      <>
        <CheckCircleOutlined style={{ color: "green", marginRight: 8 }} />
        Ativo
      </>
    ),
    value: "ATIVO",
  },
  {
    label: (
      <>
        <SyncOutlined style={{ color: "#1677ff", marginRight: 8 }} />
        Negociando
      </>
    ),
    value: "NEGOCIANDO",
  },
];

const statusOptionLabels: Record<string, ReactNode> = {
  ATIVO: (
    <>
      <CheckCircleOutlined style={{ color: "green", marginRight: 8 }} />
      Ativo
    </>
  ),
  INATIVO: (
    <>
      <CloseCircleOutlined style={{ color: "gray", marginRight: 8 }} />
      Inativo
    </>
  ),
  NEGOCIANDO: (
    <>
      <SyncOutlined style={{ color: "#1677ff", marginRight: 8 }} />
      Negociando
    </>
  ),
  NEGOCIANDO_ALTERADO: (
    <>
      <EditOutlined style={{ color: "#faad14", marginRight: 8 }} />
      Negociando Alterado
    </>
  ),
  FINALIZADO_SUCESSO: (
    <>
      <SmileOutlined style={{ color: "green", marginRight: 8 }} />
      Finalizado com Sucesso
    </>
  ),
  FINALIZADO_FRACASSO: (
    <>
      <FrownOutlined style={{ color: "red", marginRight: 8 }} />
      Finalizado com Fracasso
    </>
  ),
  SEM_INTERESSE_PERIODO_PROCURADO: (
    <>
      <SearchOutlined style={{ color: "#fa8c16", marginRight: 8 }} />
      Sem interesse no periodo procurado
    </>
  ),
  SEM_INTERESSE_PERIODO_ENCONTRADO: (
    <>
      <CalendarOutlined style={{ color: "#fa541c", marginRight: 8 }} />
      Sem interesse no periodo encontrado
    </>
  ),
  DESATUALIZADO: (
    <>
      <WarningOutlined style={{ color: "#fa541c", marginRight: 8 }} />
      Desatualizado
    </>
  ),
};

const allowedStatusValues = new Set(statusOptions.map((option) => option.value));

interface ModalAlterarStatusProps {
  isModalOpen: boolean;
  handleFechar: () => void;
  compatibilidadeId: number; // ajuste conforme o tipo usado
  buscarComp: () => void;
  compatibilidadeStatus: string;
}

export default function ModalAlterarStatus({
  isModalOpen,
  handleFechar,
  compatibilidadeId,
  buscarComp,
  compatibilidadeStatus,
}: ModalAlterarStatusProps) {
  const [dataCompatibilidade, setCompatibilidade] = useState<
    Partial<IndicacaoParceiro>
  >({});

  // Estados para controlar as exibições
  const [errorDisplayed, setErrorDisplayed] = useState(false);
  const [successDisplayed, setSuccessDisplayed] = useState(false);

  const handleStatusChange = (value: string) => {
    setCompatibilidade({
      status: value,
    });
  };

  const { atualizar, isLoading, error, success, resetError, resetSuccess } =
    useAtualizarCompatibilidade();

  // Exibe a Mensagem de  Erro
  const { showSuccess, showError, contextHolder } = useMessage();

  const currentStatusOption =
    compatibilidadeStatus && !allowedStatusValues.has(compatibilidadeStatus)
      ? [
          {
            label: statusOptionLabels[compatibilidadeStatus] ?? compatibilidadeStatus,
            value: compatibilidadeStatus,
            disabled: true,
          },
        ]
      : [];

  const statusOptionsToDisplay = [...currentStatusOption, ...statusOptions];

  useEffect(() => {
    if (isModalOpen) {
      setCompatibilidade({
        status: allowedStatusValues.has(compatibilidadeStatus)
          ? compatibilidadeStatus
          : undefined,
      });
    }
  }, [isModalOpen, compatibilidadeStatus]);

  const handleUpdateStatus = () => {
    if (!dataCompatibilidade.status || !allowedStatusValues.has(dataCompatibilidade.status)) {
      showError("Só é permitido alterar para os status Ativo ou Negociando.");
      return;
    }

    atualizar(compatibilidadeId, dataCompatibilidade);
  };

  useEffect(() => {
    if (success && !successDisplayed) {
      showSuccess(success);
      setSuccessDisplayed(true);
      // Limpa estado de sucesso após 3 segundos e recarrega a página
      setTimeout(() => {
        setSuccessDisplayed(false);
        resetSuccess();
        handleFechar();
        buscarComp();
      }, 3000);
    }
    // Exibe a mensagem de erro
    if (error && !errorDisplayed) {
      showError(error);
      setErrorDisplayed(true);

      // Limpa estado de error após 3 segundos
      setTimeout(() => {
        setErrorDisplayed(false);
        resetError();
      }, 3000);
    }
  });

  return (
    <Modal
      open={isModalOpen}
      onCancel={handleFechar}
      cancelText="Cancelar"
      okText="Alterar Status"
      onOk={handleUpdateStatus}
      confirmLoading={isLoading}
      okButtonProps={{
        style: {
          backgroundColor: "#003644",
          borderColor: "#003644",
          color: "white",
        }, // Botão "Ativar" com fundo verde
      }}
      cancelButtonProps={{
        style: {
          backgroundColor: "#f44336",
          borderColor: "#f44336",
          color: "white",
        }, // Botão "Cancelar" com fundo vermelho
      }}
    >
      <>{contextHolder}</>
      <Card
        title={<Title>Alterar Status</Title>}
        style={{
          width: "100%",
          marginTop: "20px",
          borderRadius: "10px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}
      >
        <ContainerModal>
          <ItemWrapper>
            <ItemTextModal>Status Atual</ItemTextModal>
            <Select
              style={{ width: "100%" }}
              value={dataCompatibilidade.status}
              onChange={handleStatusChange}
              placeholder="Selecione o novo status"
              options={statusOptionsToDisplay}
            />
          </ItemWrapper>
        </ContainerModal>
      </Card>
    </Modal>
  );
}
